import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Search, MapPin, ArrowRight, Ticket, Home as HomeIcon, X, Filter } from 'lucide-react';
import axios from '../../utils/axiosConfig';
import { getClientTypeMeta } from '../../utils/clientTypes';

const ExploreSalons = () => {
    const [salons, setSalons] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterType, setFilterType] = useState('all');
    const navigate = useNavigate();

    useEffect(() => {
        fetchSalons();
    }, []);

    const fetchSalons = async () => {
        try {
            const { data } = await axios.get('/api/clients');
            setSalons(data);
        } catch (err) {
            console.error('Error fetching salons:', err);
        } finally {
            setLoading(false);
        }
    };

    const filteredSalons = salons.filter(s => {
        const matchesSearch = 
            s.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            s.city?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            s.location?.toLowerCase().includes(searchTerm.toLowerCase());
        
        const matchesType = filterType === 'all' || s.organizationType === filterType || (filterType === 'producer' && s.organizationType === 'events');
        
        return matchesSearch && matchesType;
    });

    return (
        <div className="min-h-screen bg-[#080808] text-white selection:bg-primary-orange/30 pb-20">
            {/* Background Decoration */}
            <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
                <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-primary-orange/5 rounded-full blur-[120px] animate-glow" />
                <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-blue-500/5 rounded-full blur-[120px] animate-glow" style={{ animationDelay: '2s' }} />
            </div>

            {/* Header / Navbar */}
            <header className="fixed top-0 inset-x-0 z-50 bg-[#080808]/60 backdrop-blur-xl border-b border-white/5">
                <nav className="mx-auto max-w-7xl px-6 h-20 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2 group">
                        <div className="w-10 h-10 bg-brand-gradient rounded-xl flex items-center justify-center shadow-lg shadow-orange-500/20 group-hover:scale-110 transition-transform">
                            <Ticket className="text-white" size={20} />
                        </div>
                        <span className="font-black tracking-tighter text-2xl">PASSAR</span>
                    </Link>
                    <Link to="/" className="text-sm font-bold text-white/40 hover:text-white transition-colors flex items-center gap-2">
                        <HomeIcon size={16} />
                        <span>Inicio</span>
                    </Link>
                </nav>
            </header>

            {/* Hero / Search Section */}
            <div className="relative pt-40 pb-20 px-6 border-b border-white/5 bg-white/[0.01]">
                <div className="mx-auto max-w-7xl relative z-10">
                    <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-12">
                        <div>
                            <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/5 border border-white/10 rounded-full mb-4">
                                <Search size={12} className="text-primary-orange" />
                                <span className="text-[10px] font-black uppercase tracking-[2px] text-white/60">Descubrí</span>
                            </div>
                            <h1 className="text-4xl sm:text-6xl font-black tracking-tight mb-2 uppercase">Explorar <span className="text-gradient leading-relaxed">Espacios</span></h1>
                            <p className="text-white/40 font-medium tracking-tight">Busca salones, productoras, organizadores y complejos deportivos.</p>
                        </div>
                    </div>

                    {/* Search & Filters */}
                    <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
                        <div className="lg:col-span-3 relative group">
                            <Search className="absolute left-6 top-1/2 -translate-y-1/2 text-white/20 group-focus-within:text-primary-orange transition-colors" size={20} />
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Busca por nombre, ciudad o tipo de espacio..."
                                className="w-full bg-[#121212] border border-white/10 rounded-[24px] py-5 pl-16 pr-6 text-lg font-medium outline-none focus:border-primary-orange/50 transition-all shadow-2xl"
                            />
                            {searchTerm && (
                                <button onClick={() => setSearchTerm('')} className="absolute right-6 top-1/2 -translate-y-1/2 p-2 hover:bg-white/5 rounded-full text-white/40">
                                    <X size={18} />
                                </button>
                            )}
                        </div>
                        <div className="flex flex-wrap gap-2">
                            <button 
                                onClick={() => setFilterType('all')}
                                className={`flex-1 px-4 py-5 rounded-[24px] font-black text-[10px] uppercase tracking-widest transition-all ${filterType === 'all' ? 'bg-white text-black' : 'bg-white/5 border border-white/10 text-white/40'}`}
                            >
                                Todos
                            </button>
                            <button
                                onClick={() => setFilterType('venue')}
                                className={`flex-1 px-4 py-5 rounded-[24px] font-black text-[10px] uppercase tracking-widest transition-all ${filterType === 'venue' ? 'bg-primary-orange text-white' : 'bg-white/5 border border-white/10 text-white/40'}`}
                            >
                                Salones
                            </button>
                            <button
                                onClick={() => setFilterType('producer')}
                                className={`flex-1 px-4 py-5 rounded-[24px] font-black text-[10px] uppercase tracking-widest transition-all ${filterType === 'producer' ? 'bg-cyan-500 text-white' : 'bg-white/5 border border-white/10 text-white/40'}`}
                            >
                                Productoras
                            </button>
                            <button 
                                onClick={() => setFilterType('sports')}
                                className={`flex-1 px-4 py-5 rounded-[24px] font-black text-[10px] uppercase tracking-widest transition-all ${filterType === 'sports' ? 'bg-blue-500 text-white' : 'bg-white/5 border border-white/10 text-white/40'}`}
                            >
                                Deportes
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Grid de Resultados */}
            <div className="mx-auto max-w-7xl px-6 pt-12 relative z-10">
                {loading ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {[...Array(6)].map((_, i) => (
                            <div key={i} className="aspect-[1.5/1] bg-white/5 rounded-[40px] animate-pulse" />
                        ))}
                    </div>
                ) : filteredSalons.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {filteredSalons.map((s, idx) => (
                            <div 
                                key={s._id} 
                                className="group bg-glass border border-white/5 rounded-[40px] overflow-hidden hover:border-white/20 transition-all duration-500 hover:-translate-y-2 animate-fade-in-up"
                                style={{ animationDelay: `${idx * 0.05}s` }}
                            >
                                <div className="p-8 flex flex-col h-full">
                                    <div className="flex items-start justify-between mb-6">
                                        <div className="w-16 h-16 rounded-2xl bg-white/5 border border-white/10 overflow-hidden flex-shrink-0">
                                            {s.logoUrl ? (
                                                <img src={s.logoUrl} alt={s.nombre} className="w-full h-full object-cover" />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center text-white/20 font-black italic">P</div>
                                            )}
                                        </div>
                                        <div className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${s.organizationType === 'sports' ? 'bg-blue-500/10 text-blue-400 border border-blue-500/20' : s.organizationType === 'venue' ? 'bg-primary-orange/10 text-primary-orange border border-primary-orange/20' : 'bg-cyan-500/10 text-cyan-300 border border-cyan-500/20'}`}>
                                            {s.organizationShortLabel || getClientTypeMeta(s.organizationType).shortLabel}
                                        </div>
                                    </div>

                                    <h3 className="text-2xl font-black text-white mb-2 group-hover:text-primary-orange transition-colors">{s.nombre}</h3>
                                    <div className="flex items-center gap-2 text-white/40 text-sm font-medium mb-8">
                                        <MapPin size={14} className="text-primary-orange/50" />
                                        <span>{s.city || s.location || 'Sin ubicación'}</span>
                                    </div>

                                    <Link 
                                        to={`/${s.slug}`}
                                        className="mt-auto flex items-center justify-center gap-2 w-full py-4 bg-white/5 border border-white/10 rounded-2xl font-black text-[11px] uppercase tracking-[2px] text-white hover:bg-white hover:text-black transition-all"
                                    >
                                        Visitar Espacio
                                        <ArrowRight size={14} />
                                    </Link>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="py-20 text-center bg-white/[0.02] border border-dashed border-white/10 rounded-[48px]">
                        <p className="text-white/20 font-black uppercase tracking-widest">No encontramos espacios con ese nombre</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ExploreSalons;
